import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined"
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import { Link } from "react-router-dom"
import { apiFetch } from "@/hooks/apiFetch"
import { toast } from "sonner"

const StatusControl = ({
  order,
  canEdit,
  kind,
  configKey,
  icon: Icon,
  color,
  onSaved,
}) => {
  const field = `${kind}_status`
  const label = kind === "delivery" ? "Delivery Status" : "Payment Status"
  const savedLabel = order[`${field}_label`] || "Pending"
  const {
    data: options = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [configKey, "website-order-options"],
    queryFn: async ({ signal }) => {
      const statuses = []
      let page = 1
      let lastPage = 1
      do {
        const result = await apiFetch(
          `settings?filter[key]=${configKey}&sort_order=asc&per_page=100&page=${page}`,
          { signal }
        )
        statuses.push(...(result.data?.data || []))
        lastPage = result.data?.meta?.last_page || 1
        page++
      } while (page <= lastPage)
      return statuses.filter((status) => status.value?.trim())
    },
  })
  const pendingOption = options.find(
    (option) => option.value.trim().toLowerCase() === "pending"
  )
  const currentValue =
    order[`${field}_id`] != null
      ? String(order[`${field}_id`])
      : savedLabel === "Pending"
        ? pendingOption
          ? String(pendingOption.id)
          : "pending"
        : "legacy"
  const [selected, setSelected] = useState(currentValue)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState("")
  useEffect(() => {
    setSelected(currentValue)
    setError("")
  }, [order.id, currentValue])
  const hasChanges = selected !== currentValue

  const save = async () => {
    setIsSaving(true)
    setError("")
    try {
      const result = await apiFetch(`order/${order.id}/${kind}-status`, {
        method: "PUT",
        body: JSON.stringify({
          [`${field}_id`]: selected === "pending" ? null : Number(selected),
        }),
      })
      onSaved({
        [`${field}_id`]: result.data[`${field}_id`],
        [field]: result.data[field],
        [`${field}_label`]: result.data[`${field}_label`],
      })
      toast.success(`${label} saved`)
    } catch (error) {
      setError(
        error?.error?.[`${field}_id`]?.[0] ||
          error?.message ||
          `Could not save ${label.toLowerCase()}.`
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <TextField
          select
          fullWidth
          size="small"
          sx={{ minWidth: 0, flex: 1 }}
          label={label}
          value={selected}
          onChange={(event) => {
            setSelected(event.target.value)
            setError("")
          }}
          disabled={!canEdit || isLoading || isSaving || isError}
          error={!!error || isError}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon sx={{ fontSize: 18, color }} />
              </InputAdornment>
            ),
          }}
          FormHelperTextProps={{ role: error || isError ? "alert" : undefined }}
          helperText={
            error ||
            (isError
              ? "Could not load statuses. Try again."
              : hasChanges
                ? `Saved: ${savedLabel}`
                : null)
          }
        >
          {(!pendingOption || currentValue === "pending") && (
            <MenuItem value="pending">Pending</MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={option.id} value={String(option.id)}>
              {option.value}
            </MenuItem>
          ))}
          {currentValue !== "pending" &&
            !options.some((option) => String(option.id) === currentValue) && (
              <MenuItem value={currentValue} disabled>
                {savedLabel}
              </MenuItem>
            )}
        </TextField>
        {hasChanges && (
          <Button
            variant="contained"
            size="small"
            aria-label={`Save ${label}`}
            onClick={save}
            sx={{ height: 40, boxShadow: "none", textTransform: "none" }}
            disabled={
              !canEdit ||
              isSaving ||
              isLoading ||
              isError ||
              selected === "legacy"
            }
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        )}
      </Box>
      {isError && (
        <Button size="small" onClick={() => refetch()}>
          Retry loading statuses
        </Button>
      )}
    </Box>
  )
}

export default function WebsiteOrderStatusPanel({ order, canEdit, onSaved }) {
  return (
    <Paper
      component="section"
      aria-label="Order status"
      elevation={0}
      sx={{
        mb: 2,
        p: 1.5,
        border: "1px solid #e2e8f0",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <StatusControl
          order={order}
          canEdit={canEdit}
          kind="delivery"
          configKey="sales-status"
          icon={LocalShippingOutlinedIcon}
          color="#2563eb"
          onSaved={onSaved}
        />
        <StatusControl
          order={order}
          canEdit={canEdit}
          kind="payment"
          configKey="payment-status"
          icon={PaymentsOutlinedIcon}
          color="#0f766e"
          onSaved={onSaved}
        />
      </Box>
      {!canEdit && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          Accept the order before changing delivery or payment status.
        </Typography>
      )}
    </Paper>
  )
}
